/**
 * TeammateAvatar — the AI employee's face.
 *
 * Flint's assistant is presented as a teammate you message — "Alan", a
 * plain English male name with a double AI wink: Alan Turing, and "Al"
 * reading almost as "AI" in sans-serif. The avatar is a flat
 * corporate-illustration portrait (side-parted hair, headset with mic,
 * sky shirt) used on the launcher pin/tile and the module-switcher row.
 * Honesty note: every chat surface it opens keeps the "generated by AI"
 * disclaimer — the persona humanizes the entry point, it never hides
 * that Alan is AI (system-decisions §11).
 *
 * Rename the employee by changing `AI_TEAMMATE_NAME` — every surface
 * reads it from here.
 *
 * Colors: chrome hues ride the tokens (sky plinth + shirt flip for dark
 * mode); the face uses fixed illustration tones (skin, hair, gear) that
 * stay constant across themes — they're artwork, not UI chrome, and
 * letting `--ink` flip would give Alan white hair in dark mode.
 */
import { cn } from "@flint/ui";

/** The AI employee's display name — single source for every surface. */
export const AI_TEAMMATE_NAME = "Alan";
/** The one-line role shown under the name. */
export const AI_TEAMMATE_ROLE = "Your AI teammate";

export interface TeammateAvatarProps {
  /** Pixel size of the square avatar (default 40). */
  size?: number;
  /** Show the green "online" presence dot (launcher pin). */
  presence?: boolean;
  className?: string;
}

const SKIN = "#eab387";
const SKIN_SHADE = "#d69769";
const HAIR = "#22303f";
const GEAR = "#0f172a";

export function TeammateAvatar({ size = 40, presence = false, className }: TeammateAvatarProps) {
  return (
    <span
      className={cn("relative inline-flex shrink-0", className)}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 64 64"
        width={size}
        height={size}
        role="img"
        aria-label={`${AI_TEAMMATE_NAME}, ${AI_TEAMMATE_ROLE}`}
      >
        <defs>
          <clipPath id="teammate-plinth">
            <circle cx="32" cy="32" r="32" />
          </clipPath>
        </defs>
        {/* Plinth */}
        <circle cx="32" cy="32" r="32" fill="var(--sky-100)" />
        <g clipPath="url(#teammate-plinth)">
          {/* Shoulders / shirt */}
          <path d="M8 66c1.6-13.4 11.4-20.6 24-20.6S54.4 52.6 56 66Z" fill="var(--sky-600)" />
          {/* Collar */}
          <path d="M27.2 45.9 32 53l4.8-7.1-2.2-1.5h-5.2Z" fill="var(--paper)" />
          {/* Neck */}
          <rect x="27.2" y="34.5" width="9.6" height="12" rx="4.8" fill={SKIN} />
          {/* Chin shadow on the neck */}
          <path d="M27.2 36.5c3.2 2 6.4 2 9.6 0v4.2c-3.2 1.7-6.4 1.7-9.6 0Z" fill={SKIN_SHADE} />
          {/* Ears */}
          <circle cx="18.9" cy="27.4" r="3" fill={SKIN} />
          <circle cx="45.1" cy="27.4" r="3" fill={SKIN} />
          {/* Head */}
          <ellipse cx="32" cy="26" rx="13.2" ry="14" fill={SKIN} />
          {/* Hair — short cut with a side part */}
          <path
            d="M18.8 26.6c-1-10 5.5-16.9 13.2-16.9 7.9 0 14.3 6.7 13.2 17.1-.6-2.4-1.6-4.9-3.4-6.3-2.1 1.7-5.6 2.4-8.9 2-3.9-.4-7.5-2-9.6-4-2.4 1.6-4 5.2-4.5 8.1Z"
            fill={HAIR}
          />
          {/* Eyebrows */}
          <path d="M23.7 23.9c1.6-1 3.5-1.1 5-.4" stroke={HAIR} strokeWidth="1.5" strokeLinecap="round" fill="none" />
          <path d="M35.3 23.5c1.5-.7 3.4-.6 5 .4" stroke={HAIR} strokeWidth="1.5" strokeLinecap="round" fill="none" />
          {/* Eyes */}
          <ellipse cx="26.6" cy="27.6" rx="1.5" ry="1.8" fill={GEAR} />
          <ellipse cx="37.4" cy="27.6" rx="1.5" ry="1.8" fill={GEAR} />
          {/* Nose */}
          <path d="M31.8 27.6c.6 2 .5 3.4-.6 4.4" stroke={SKIN_SHADE} strokeWidth="1.4" strokeLinecap="round" fill="none" />
          {/* Smile */}
          <path d="M27.2 34.8c3 2.4 6.6 2.4 9.6 0" stroke={GEAR} strokeWidth="1.6" strokeLinecap="round" fill="none" />
          {/* Headset — band, earcups, mic boom */}
          <path d="M17.5 24.5C18 15.6 24.1 9.8 32 9.8s14 5.8 14.5 14.7" stroke={GEAR} strokeWidth="2.4" strokeLinecap="round" fill="none" />
          <rect x="15" y="22.6" width="5" height="9.4" rx="2.5" fill={GEAR} />
          <rect x="44" y="22.6" width="5" height="9.4" rx="2.5" fill={GEAR} />
          <path d="M46.5 31.8c0 5.4-4.2 8.4-9.2 8.9" stroke={GEAR} strokeWidth="1.9" strokeLinecap="round" fill="none" />
          <circle cx="36.6" cy="40.9" r="2.1" fill={GEAR} />
        </g>
      </svg>
      {presence ? (
        <span
          aria-hidden
          title="Online"
          className="absolute -bottom-0.5 -right-0.5 rounded-full bg-ok ring-2 ring-paper"
          style={{ width: Math.max(10, size * 0.22), height: Math.max(10, size * 0.22) }}
        />
      ) : null}
    </span>
  );
}
